import React, { useState, useEffect } from "react";
import axios from 'axios';

const BluetoothDialer = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [deviceName, setDeviceName] = useState("Phone Tracker App");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [imei, setImei] = useState("");
  const [model, setModel] = useState("");
  const [brand, setBrand] = useState("");
  const [callerImei, setCallerImei] = useState("");
  const [receiverImei, setReceiverImei] = useState("");
  const [errorMessage, setErrorMessage] = useState(null); // Changed to null to allow JSX
  const [successMessage, setSuccessMessage] = useState("");
  const [devices, setDevices] = useState([]);
  const [currentDevice, setCurrentDevice] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("bluetooth");
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [bluetoothSupported, setBluetoothSupported] = useState(true);
  const [mapError, setMapError] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [checkingLocation, setCheckingLocation] = useState(false);

  // ✅ Google Maps API Key
  const GOOGLE_MAPS_API_KEY = "AIzaSyCxTMV0Q3yBn0Lk2NDjrYzl3rn4NWsQBL4";

  // ========== CHECK IF LOCATION IS ENABLED ==========
  const checkLocationStatus = () => {
    setCheckingLocation(true);
    setErrorMessage(null); // Clear any previous errors
    
    if (!navigator.geolocation) {
      setLocationEnabled(false);
      setCheckingLocation(false);
      return;
    }

    // Try to get current position to check if location is enabled
    navigator.geolocation.getCurrentPosition(
      () => {
        // Location is enabled and permission granted
        setLocationEnabled(true);
        setCheckingLocation(false);
        setErrorMessage(null);
      },
      (error) => {
        // Location is disabled or permission denied
        setLocationEnabled(false);
        setCheckingLocation(false);
        
        // Don't show error message here - just update status
        console.log('Location status:', error.code, error.message);
      },
      {
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: Infinity
      }
    );
  };

  // Check location status on component mount and when tab changes
  useEffect(() => {
    checkLocationStatus();
  }, [activeTab]);

  // ========== TOGGLE LOCATION ==========
  const toggleLocation = () => {
    if (locationEnabled) {
      // If location is on, we can't programmatically turn it off
      // Show instructions to turn off
      setErrorMessage(
        <div style={styles.errorContent}>
          <p>🔴 To turn off location:</p>
          <button 
            onClick={openLocationSettings}
            style={styles.settingsButton}
          >
            ⚙️ Open Location Settings
          </button>
          <p style={{fontSize: '12px', marginTop: '5px'}}>
            Then toggle location OFF in your device settings
          </p>
        </div>
      );
    } else {
      // If location is off, open settings to turn on
      openLocationSettings();
    }
  };

  // ========== OPEN LOCATION SETTINGS ==========
  const openLocationSettings = () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      if (/Android/i.test(navigator.userAgent)) {
        // Android - try to open location settings
        window.location.href = 'android.settings.LOCATION_SOURCE_SETTINGS';
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📱 Opening location settings...</p>
            <p>Please enable location manually if settings didn't open.</p>
            <button 
              onClick={() => {
                setErrorMessage(null);
                setTimeout(checkLocationStatus, 5000);
              }}
              style={styles.settingsButton}
            >
              ✓ I've enabled location
            </button>
          </div>
        );
      } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // iOS - try to open privacy settings
        window.location.href = 'App-Prefs:root=Privacy&path=LOCATION';
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📱 Opening location settings...</p>
            <p>Please enable location in: Settings → Privacy → Location Services</p>
            <button 
              onClick={() => {
                setErrorMessage(null);
                setTimeout(checkLocationStatus, 5000);
              }}
              style={styles.settingsButton}
            >
              ✓ I've enabled location
            </button>
          </div>
        );
      } else {
        // Other mobile
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📱 Please enable location in your device settings</p>
            <button 
              onClick={() => {
                setErrorMessage(null);
                checkLocationStatus();
              }}
              style={styles.settingsButton}
            >
              ✓ Check again
            </button>
          </div>
        );
      }
    } else {
      // Desktop - show browser-specific instructions with button
      const browser = getBrowser();
      let instructions = '';
      
      switch(browser) {
        case 'chrome':
          instructions = 'Click the lock icon in the address bar → Site Settings → Location → Allow';
          break;
        case 'edge':
          instructions = 'Click the lock icon in the address bar → Site permissions → Location → Allow';
          break;
        case 'firefox':
          instructions = 'Click the lock icon in the address bar → Connection secure → More information → Permissions → Access Your Location → Allow';
          break;
        case 'safari':
          instructions = 'Safari → Preferences → Websites → Location → Allow';
          break;
        default:
          instructions = 'Please enable location in your browser settings';
      }
      
      setErrorMessage(
        <div style={styles.errorContent}>
          <p>📍 {instructions}</p>
          <button 
            onClick={() => {
              setErrorMessage(null);
              checkLocationStatus();
            }}
            style={styles.settingsButton}
          >
            ✓ I've enabled location
          </button>
        </div>
      );
    }
    
    // Recheck location status after a delay
    setTimeout(() => {
      checkLocationStatus();
    }, 5000);
  };

  // ========== OPEN BLUETOOTH SETTINGS ==========
  const openBluetoothSettings = () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      if (/Android/i.test(navigator.userAgent)) {
        // Android - try to open Bluetooth settings
        window.location.href = 'android.settings.BLUETOOTH_SETTINGS';
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📱 Opening Bluetooth settings...</p>
            <button 
              onClick={() => setErrorMessage(null)}
              style={styles.settingsButton}
            >
              ✓ Close
            </button>
          </div>
        );
      } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // iOS - try to open Bluetooth settings
        window.location.href = 'App-Prefs:root=Bluetooth';
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📱 Opening Bluetooth settings...</p>
            <button 
              onClick={() => setErrorMessage(null)}
              style={styles.settingsButton}
            >
              ✓ Close
            </button>
          </div>
        );
      } else {
        // Other mobile
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📱 Please enable Bluetooth in your device settings</p>
            <button 
              onClick={() => setErrorMessage(null)}
              style={styles.settingsButton}
            >
              ✓ Close
            </button>
          </div>
        );
      }
    } else {
      // Desktop - show instructions
      if (/Windows/i.test(navigator.userAgent)) {
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📋 To enable Bluetooth on Windows:</p>
            <p>Settings → Devices → Bluetooth & other devices</p>
            <button 
              onClick={() => setErrorMessage(null)}
              style={styles.settingsButton}
            >
              ✓ Close
            </button>
          </div>
        );
      } else if (/Mac/i.test(navigator.userAgent)) {
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📋 To enable Bluetooth on Mac:</p>
            <p>System Preferences → Bluetooth</p>
            <button 
              onClick={() => setErrorMessage(null)}
              style={styles.settingsButton}
            >
              ✓ Close
            </button>
          </div>
        );
      } else {
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>📋 Please enable Bluetooth in your system settings</p>
            <button 
              onClick={() => setErrorMessage(null)}
              style={styles.settingsButton}
            >
              ✓ Close
            </button>
          </div>
        );
      }
    }
  };

  // ========== HELPER TO DETECT BROWSER ==========
  const getBrowser = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf("Chrome") > -1) return 'chrome';
    if (userAgent.indexOf("Safari") > -1) return 'safari';
    if (userAgent.indexOf("Firefox") > -1) return 'firefox';
    if (userAgent.indexOf("Edg") > -1) return 'edge';
    return 'unknown';
  };

  // ========== IMPROVED LOCATION useEffect ==========
  useEffect(() => {
    if (navigator.geolocation) {
      // Set a timeout for geolocation request
      const timeoutId = setTimeout(() => {
        setUserLocation({ lat: 6.5244, lng: 3.3792 }); // Fallback to Lagos
        console.log('📍 Using fallback location (Lagos) due to timeout');
      }, 5000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationEnabled(true);
          setErrorMessage(null);
          console.log('📍 Got user location successfully');
        },
        (error) => {
          clearTimeout(timeoutId);
          console.error('Error fetching location:', error.code, error.message);
          
          setUserLocation({ lat: 6.5244, lng: 3.3792 }); // Fallback to Lagos
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setUserLocation({ lat: 6.5244, lng: 3.3792 });
    }
  }, []);

  // Check if Bluetooth is available
  useEffect(() => {
    if (!navigator.bluetooth) {
      setBluetoothSupported(false);
    }
  }, []);

  // Initialize Google Maps when tracking tab is active
  useEffect(() => {
    if (activeTab === "tracking" && !mapLoaded && !mapError && userLocation) {
      loadGoogleMapsScript();
    }
  }, [activeTab, mapLoaded, mapError, userLocation]);

  const loadGoogleMapsScript = () => {
    if (window.google && window.google.maps) {
      initMap();
      setMapLoaded(true);
      return;
    }

    const existingScript = document.getElementById('googleMapsScript');
    if (existingScript) {
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogle);
          initMap();
          setMapLoaded(true);
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async&callback=initMap`;
    script.id = 'googleMapsScript';
    script.async = true;
    script.defer = true;
    
    window.initMap = initMap;
    
    script.onerror = () => {
      console.error("Error loading Google Maps API script.");
      setMapError("Failed to load Google Maps. Please check your API key and ensure billing is enabled.");
    };
    
    document.body.appendChild(script);
  };

  const initMap = () => {
    const mapElement = document.getElementById('map');
    if (mapElement && window.google && !map && userLocation) {
      try {
        const newMap = new window.google.maps.Map(mapElement, {
          center: userLocation,
          zoom: 12,
          mapTypeId: 'roadmap',
          mapTypeControl: true,
          fullscreenControl: true,
          streetViewControl: true,
        });

        // Add user location marker
        showOnMap(userLocation, 'Your Location', 'blue', newMap);

        setMap(newMap);
        setMapLoaded(true);
        setMapError(null);
      } catch (error) {
        console.error("Map initialization error:", error);
        setMapError("Failed to initialize map: " + error.message);
      }
    }
  };

  // ========== IMPROVED showOnMap FUNCTION ==========
  const showOnMap = (coordinates, title = 'Phone Location', color = 'red', targetMap = map) => {
    const mapToUse = targetMap || map;
    if (!mapToUse || !window.google) return;

    const position = { lat: coordinates.lat, lng: coordinates.lng };
    mapToUse.setCenter(position);
    mapToUse.setZoom(15);
    
    // Remove old marker
    if (marker && !targetMap) {
      marker.setMap(null);
    }
    
    try {
      let newMarker;
      
      // Try using AdvancedMarkerElement (modern API)
      if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
        const { AdvancedMarkerElement } = window.google.maps.marker;
        
        newMarker = new AdvancedMarkerElement({
          position: position,
          map: mapToUse,
          title: title,
        });
      } else {
        // Fallback to classic marker
        newMarker = new window.google.maps.Marker({
          position: position,
          map: mapToUse,
          title: title,
          animation: window.google.maps.Animation.DROP,
          icon: {
            url: `http://maps.google.com/mapfiles/ms/icons/${color}-dot.png`
          }
        });
      }
      
      if (!targetMap) {
        setMarker(newMarker);
      }
      
      // Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <h3 style="margin: 0 0 5px 0;">📱 ${title}</h3>
            <p style="margin: 5px 0;"><strong>Lat:</strong> ${coordinates.lat.toFixed(6)}</p>
            <p style="margin: 5px 0;"><strong>Lng:</strong> ${coordinates.lng.toFixed(6)}</p>
            <p style="margin: 5px 0;"><small>${new Date().toLocaleString()}</small></p>
          </div>
        `
      });
      
      infoWindow.open(mapToUse, newMarker);
      
    } catch (error) {
      console.error('Error creating marker:', error);
    }
  };

  // ========== AUTO LOCATION REFRESH WITH SETTINGS BUTTON ==========
  const refreshLocation = () => {
    setLoading(true);
    setErrorMessage(null); // Clear any previous errors
    
    if (navigator.geolocation) {
      console.log("📍 Requesting device location for tracking...");
      
      // Show a message that we're getting location
      setSuccessMessage("📍 Getting your current location...");
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Get the current device location
          const currentLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          };
          
          console.log("📍 Device location obtained:", currentLocation);
          
          // Update state with new location
          setUserLocation(currentLocation);
          setLocationEnabled(true);
          
          // Show success message with accuracy
          setSuccessMessage(`📍 Location updated! Accuracy: ±${Math.round(currentLocation.accuracy)}m`);
          
          // Update map if it exists
          if (map) {
            map.setCenter(currentLocation);
            map.setZoom(16); // Zoom in closer for accuracy
            
            // Add or update marker with current location
            showOnMap(currentLocation, 'Your Current Location', 'blue');
            
            // Add accuracy circle if using advanced maps
            if (window.google && window.google.maps) {
              // Remove old accuracy circle if exists
              if (window.accuracyCircle) {
                window.accuracyCircle.setMap(null);
              }
              
              // Draw accuracy circle
              window.accuracyCircle = new window.google.maps.Circle({
                strokeColor: '#4285F4',
                strokeOpacity: 0.3,
                strokeWeight: 1,
                fillColor: '#4285F4',
                fillOpacity: 0.15,
                map: map,
                center: currentLocation,
                radius: currentLocation.accuracy // Use actual accuracy
              });
            }
          }
          
          // **AUTO-TRACK after getting location** 
          // If we have a phone number to track, automatically track it
          if (phoneNumber) {
            setSuccessMessage(`📍 Got your location! Now tracking ${phoneNumber}...`);
            trackPhoneByNumber(phoneNumber);
          } else if (imei) {
            setSuccessMessage(`📍 Got your location! Now tracking IMEI: ${imei}...`);
            trackPhoneByIMEI(imei);
          } else {
            setSuccessMessage(`📍 Location updated! Enter a phone number or IMEI to track.`);
          }
          
          setLoading(false);
        },
        (error) => {
          console.error('❌ Error getting device location:', error.code, error.message);
          setLocationEnabled(false);
          
          // Handle specific errors with user-friendly messages and settings button
          switch(error.code) {
            case 1: // PERMISSION_DENIED
              setErrorMessage(
                <div style={styles.errorContent}>
                  <p>❌ Location permission denied.</p>
                  <button 
                    onClick={openLocationSettings}
                    style={styles.settingsButton}
                  >
                    ⚙️ Open Location Settings
                  </button>
                </div>
              );
              break;
            case 2: // POSITION_UNAVAILABLE
              setErrorMessage(
                <div style={styles.errorContent}>
                  <p>❌ Location unavailable.</p>
                  <button 
                    onClick={openLocationSettings}
                    style={styles.settingsButton}
                  >
                    ⚙️ Open Location Settings
                  </button>
                </div>
              );
              break;
            case 3: // TIMEOUT
              setErrorMessage(
                <div style={styles.errorContent}>
                  <p>❌ Location request timed out.</p>
                  <button 
                    onClick={refreshLocation}
                    style={styles.settingsButton}
                  >
                    🔄 Try Again
                  </button>
                </div>
              );
              break;
            default:
              setErrorMessage(
                <div style={styles.errorContent}>
                  <p>❌ Failed to get location: {error.message}</p>
                  <button 
                    onClick={openLocationSettings}
                    style={styles.settingsButton}
                  >
                    ⚙️ Open Location Settings
                  </button>
                </div>
              );
          }
          
          setLoading(false);
        },
        {
          enableHighAccuracy: true, // Use GPS for best accuracy
          timeout: 15000, // Wait up to 15 seconds
          maximumAge: 0 // Always get fresh location, don't use cached
        }
      );
    } else {
      setErrorMessage(
        <div style={styles.errorContent}>
          <p>❌ Geolocation is not supported by your browser.</p>
          <p>Please use Chrome, Edge, Firefox, or Safari.</p>
        </div>
      );
      setLoading(false);
    }
  };

  // Function to request Bluetooth device and connect
  const connectToBluetooth = async () => {
    if (!bluetoothSupported) {
      setErrorMessage(
        <div style={styles.errorContent}>
          <p>❌ Bluetooth is not supported in this browser.</p>
          <p>Please use Chrome, Edge, or Opera.</p>
        </div>
      );
      return;
    }

    try {
      setErrorMessage(null);
      setSuccessMessage("");
      console.log("Requesting Bluetooth device...");
      
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ["00001101-0000-1000-8000-00805f9b34fb"],
      });

      console.log("Device selected:", device.name);
      setDeviceName(device.name || "Unknown Device");
      setDevices((prevDevices) => [
        ...prevDevices,
        { name: device.name || "Unknown", id: device.id },
      ]);

      const server = await device.gatt.connect();
      console.log("Connected to GATT server:", server);
      setIsConnected(true);
      setCurrentDevice(device);

      // In a real app, you'd read the phone number from the device
      // For demo, use a sample number
      const detectedNumber = "+2348068526093";
      setPhoneNumber(detectedNumber);
      
      setSuccessMessage(`Connected to ${device.name}. Phone number detected: ${detectedNumber}`);
      
    } catch (error) {
      console.error("Bluetooth connection failed:", error);
      
      if (error.name === "NotFoundError") {
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>❌ No Bluetooth device selected.</p>
            <button 
              onClick={connectToBluetooth}
              style={styles.settingsButton}
            >
              🔄 Try Again
            </button>
          </div>
        );
      } else if (error.name === "SecurityError") {
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>❌ Bluetooth permission denied.</p>
            <button 
              onClick={openBluetoothSettings}
              style={styles.settingsButton}
            >
              ⚙️ Open Bluetooth Settings
            </button>
          </div>
        );
      } else if (error.message.includes("adapter")) {
        // Bluetooth adapter not available - show settings button
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>❌ Bluetooth adapter not available.</p>
            <button 
              onClick={openBluetoothSettings}
              style={styles.settingsButton}
            >
              ⚙️ Open Bluetooth Settings
            </button>
          </div>
        );
      } else {
        setErrorMessage(
          <div style={styles.errorContent}>
            <p>❌ Bluetooth connection failed: {error.message}</p>
            <button 
              onClick={connectToBluetooth}
              style={styles.settingsButton}
            >
              🔄 Try Again
            </button>
          </div>
        );
      }
    }
  };

  // ========== SIMULATE CALL & SCAN WITH DEBUG ==========
  const simulateCallAndScan = async () => {
    console.log("🔍 Simulate Call & Scan clicked");
    console.log("Current state values:");
    console.log("- phoneNumber:", phoneNumber);
    console.log("- callerImei:", callerImei);
    console.log("- receiverImei:", receiverImei);
    
    // Check all possible sources for a phone number
    const numberToUse = phoneNumber || callerImei || receiverImei;
    
    console.log("- numberToUse:", numberToUse);
    
    if (!numberToUse) {
      console.log("❌ No phone number found in any field");
      setErrorMessage("❌ Please enter a phone number in the Bluetooth tab or Call Tracking tab first.");
      return;
    }

    // Validate it looks like a phone number (basic check)
    const phoneRegex = /^[\+]?[0-9]{8,15}$/;
    const cleanNumber = numberToUse.replace(/[\s\-\(\)]/g, '');
    
    console.log("- cleanNumber:", cleanNumber);
    console.log("- isValid format:", phoneRegex.test(cleanNumber));
    
    if (!phoneRegex.test(cleanNumber)) {
      setErrorMessage("❌ Invalid phone number format. Please use international format (e.g., +2348068526093)");
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(`📞 Simulating call to ${cleanNumber} and scanning network...`);

    try {
      console.log("📡 Sending request to backend with:", { phoneNumber: cleanNumber });
      
      // First, dial the number (simulate)
      dialPhoneNumber(cleanNumber);
      
      // Then scan and track
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/dialAndScan', {
        phoneNumber: cleanNumber,
        isCallPickedUp: true
      });

      console.log("✅ Backend response:", response.data);
      
      if (response.data.success) {
        setSuccessMessage(`✅ Call simulated! Found ${response.data.devicesFound} devices. Location accuracy: ±${response.data.location.accuracy}m`);
        
        // Show on map if coordinates available
        if (response.data.location && response.data.location.coordinates && map) {
          showOnMap(
            { 
              lat: response.data.location.coordinates.lat, 
              lng: response.data.location.coordinates.lng 
            },
            'Simulated Call',
            'purple'
          );
        } else if (response.data.location && !response.data.location.coordinates && map) {
          // Handle legacy format
          showOnMap(
            { 
              lat: response.data.location.lat, 
              lng: response.data.location.lng 
            },
            'Simulated Call',
            'purple'
          );
        }
        
        setTrackingData(response.data);
        setActiveTab("tracking");
      } else {
        console.log("❌ Backend returned error:", response.data.error);
        setErrorMessage("❌ Failed to simulate call: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error('❌ Simulate call error:', error);
      console.error('Error response:', error.response?.data);
      setErrorMessage('❌ Failed to simulate call: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ========== IMPROVED DIAL FUNCTION ==========
  const dialPhoneNumber = (number) => {
    // Check if number exists
    if (!number) {
      setErrorMessage("❌ No phone number to dial. Please connect to Bluetooth or enter a number.");
      return;
    }

    // Clean the number (remove spaces, dashes, etc.)
    const cleanNumber = number.replace(/[\s\-\(\)]/g, '');
    
    // Validate phone number format (simple check)
    const phoneRegex = /^[\+]?[0-9]{8,15}$/;
    if (!phoneRegex.test(cleanNumber)) {
      setErrorMessage('❌ Invalid phone number format. Please use international format (e.g., +2348068526093)');
      return;
    }

    try {
      const phoneLink = `tel:${cleanNumber}`;
      
      // Check if we're on a mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      console.log(`Dialing ${cleanNumber}...`);
      
      if (isMobile) {
        // On mobile, open the dialer
        window.location.href = phoneLink;
        setSuccessMessage(`📞 Opening dialer for ${cleanNumber}...`);
      } else {
        // On desktop, show simulation
        setSuccessMessage(`📞 SIMULATION: Would call ${cleanNumber} on a mobile device. Starting location tracking...`);
      }
      
      // After dialing, update location (simulate call pickup after 5 seconds)
      setTimeout(() => {
        updateLocationAfterCall(cleanNumber);
      }, 5000);
      
    } catch (error) {
      console.error('Error dialing number:', error);
      setErrorMessage('❌ Failed to dial number: ' + error.message);
    }
  };

  // Update location after call is picked up
  const updateLocationAfterCall = async (number) => {
    try {
      setSuccessMessage(`📡 Call in progress for ${number}. Updating location...`);
      
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/phones/update-location-direct', {
        phoneNumber: number,
        isCallPickedUp: true
      });
      
      console.log('Location updated after call:', response.data);
      setSuccessMessage(`📍 Phone location updated! Accuracy: ±${response.data.location.accuracy}m`);
      
      // Track the phone to show on map
      await trackPhoneByNumber(number);
      
    } catch (error) {
      console.error('Error updating location after call:', error);
      setErrorMessage('❌ Failed to update location after call: ' + (error.response?.data?.message || error.message));
    }
  };

  // Register phone in database
  const registerPhone = async () => {
    if (!phoneNumber || !imei || !model || !brand) {
      setErrorMessage("Please fill in all phone details");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/phones/register', {
        phoneNumber,
        imei,
        model,
        brand,
        lastKnownLocation: userLocation ? {
          latitude: userLocation.lat,
          longitude: userLocation.lng
        } : null
      });

      setSuccessMessage(response.data.message);
      setErrorMessage(null);
      
      // Auto-track after registration
      await trackPhoneByNumber(phoneNumber);
      
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Error registering phone');
    } finally {
      setLoading(false);
    }
  };

  // Track phone by number
  const trackPhoneByNumber = async (number) => {
    setLoading(true);
    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/track-phone', {
        phoneNumber: number
      });

      const data = response.data;
      setTrackingData(data);

      if (data.success && data.location) {
        showOnMap(data.location.coordinates, 'Tracked Phone', 'red');
        setActiveTab("tracking");
        setSuccessMessage('Phone tracked successfully!');
      } else {
        setErrorMessage("No location found for this phone number.");
      }
    } catch (error) {
      console.error("Tracking failed:", error);
      setErrorMessage("Failed to track phone: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Track phone by IMEI
  const trackPhoneByIMEI = async (imei) => {
    setLoading(true);
    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/track-phone', {
        imei: imei
      });

      const data = response.data;
      setTrackingData(data);

      if (data.success && data.location) {
        showOnMap(data.location.coordinates, 'Tracked Phone', 'red');
        setActiveTab("tracking");
        setSuccessMessage('Phone tracked successfully!');
      } else {
        setErrorMessage("No location found for this IMEI.");
      }
    } catch (error) {
      console.error("Tracking failed:", error);
      setErrorMessage("Failed to track phone: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Track call between two phones
  const trackCall = async () => {
    if (!callerImei && !receiverImei) {
      setErrorMessage("Please enter at least one IMEI");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/phones/call-location', {
        callerImei: callerImei || undefined,
        receiverImei: receiverImei || undefined
      });

      const { callerLocation, receiverLocation } = response.data;
      
      if (callerLocation && map) {
        showOnMap(
          { lat: callerLocation.latitude, lng: callerLocation.longitude },
          'Caller',
          'green'
        );
      }
      
      if (receiverLocation && map) {
        showOnMap(
          { lat: receiverLocation.latitude, lng: receiverLocation.longitude },
          'Receiver',
          'red'
        );
      }
      
      setSuccessMessage('Call locations retrieved successfully');
      
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Error tracking call');
    } finally {
      setLoading(false);
    }
  };

  // Disconnect Bluetooth
  const disconnectBluetooth = async () => {
    if (currentDevice && currentDevice.gatt && currentDevice.gatt.connected) {
      try {
        currentDevice.gatt.disconnect();
        console.log(`Disconnected from ${deviceName}`);
        setIsConnected(false);
        setCurrentDevice(null);
        setSuccessMessage(`Disconnected from ${deviceName}`);
      } catch (error) {
        console.error("Disconnect error:", error);
      }
    }
  };

  // Render Google Maps activation guide
  const renderMapActivationGuide = () => (
    <div style={{...styles.error, padding: '20px', marginBottom: '20px', backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeeba'}}>
      <h3 style={{marginTop: 0}}>🗺️ Google Maps Needs Activation</h3>
      <p>Your API key is valid but needs to be activated. Follow these steps:</p>
      <ol style={{textAlign: 'left', marginTop: '10px'}}>
        <li>Go to <a href="https://console.cloud.google.com/apis/library/maps-backend.googleapis.com?project=phone-tracking-app-489116" target="_blank" rel="noopener noreferrer" style={{color: '#007bff'}}>Google Cloud Console</a></li>
        <li>Click <strong>ENABLE</strong> for Maps JavaScript API</li>
        <li>Go to <strong>Billing</strong> and enable billing (free tier available)</li>
        <li>Wait 5 minutes and refresh</li>
      </ol>
    </div>
  );

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📱 Phone Tracker & Bluetooth Dialer</h1>

      {/* Tab Navigation */}
      <div style={styles.tabContainer}>
        <button
          style={{...styles.tab, ...(activeTab === 'bluetooth' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('bluetooth')}
        >
          📱 Bluetooth Dialer
        </button>
        <button
          style={{...styles.tab, ...(activeTab === 'register' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('register')}
        >
          📝 Register Phone
        </button>
        <button
          style={{...styles.tab, ...(activeTab === 'tracking' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('tracking')}
        >
          🗺️ Track Phone
        </button>
        <button
          style={{...styles.tab, ...(activeTab === 'call-track' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('call-track')}
        >
          📞 Call Tracking
        </button>
      </div>

      {/* Bluetooth Tab */}
      {activeTab === 'bluetooth' && (
        <div style={styles.tabContent}>
          <div style={styles.buttonContainer}>
            <button
              onClick={connectToBluetooth}
              style={styles.button}
              disabled={isConnected}
            >
              {isConnected ? '✅ Connected' : '🔌 Connect to Bluetooth'}
            </button>
            {isConnected && (
              <button
                onClick={disconnectBluetooth}
                style={{...styles.button, ...styles.dangerButton}}
              >
                Disconnect
              </button>
            )}
          </div>

          <h2 style={styles.subtitle}>📋 Available Bluetooth Devices</h2>
          {devices.length > 0 ? (
            <ul style={styles.deviceList}>
              {devices.map((device, index) => (
                <li key={index} style={styles.deviceItem}>
                  <strong>{device.name}</strong> <span style={{color: '#666'}}>(ID: {device.id})</span>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{textAlign: 'center', color: '#666'}}>No devices available. Click "Connect to Bluetooth" to scan.</p>
          )}

          {isConnected && (
            <div style={styles.connectedInfo}>
              <h3>✅ Connected to {deviceName}</h3>
              <p><strong>Detected Phone Number:</strong> {phoneNumber || 'Not detected'}</p>
              {phoneNumber && (
                <div style={{display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap'}}>
                  <button
                    onClick={() => dialPhoneNumber(phoneNumber)}
                    style={{...styles.button, backgroundColor: '#28a745'}}
                  >
                    📞 Dial {phoneNumber}
                  </button>
                  <button
                    onClick={() => trackPhoneByNumber(phoneNumber)}
                    style={{...styles.button, backgroundColor: '#17a2b8'}}
                  >
                    🎯 Track Phone
                  </button>
                  {/* Simulate Call & Scan Button */}
                  <button
                    onClick={simulateCallAndScan}
                    disabled={loading}
                    style={{...styles.button, backgroundColor: '#ff9800'}}
                  >
                    {loading ? '🔄 Simulating...' : '📱 Simulate Call & Scan'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Register Phone Tab */}
      {activeTab === 'register' && (
        <div style={styles.tabContent}>
          <h2 style={styles.subtitle}>📝 Register New Phone</h2>
          <div style={styles.form}>
            <input
              type="text"
              placeholder="Phone Number (e.g., +2348068526093)"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              style={styles.input}
            />
            <input
              type="text"
              placeholder="IMEI Number"
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              style={styles.input}
            />
            <input
              type="text"
              placeholder="Model (e.g., iPhone 12)"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={styles.input}
            />
            <input
              type="text"
              placeholder="Brand (e.g., Apple)"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              style={styles.input}
            />
            <button
              onClick={registerPhone}
              disabled={loading}
              style={styles.trackButton}
            >
              {loading ? 'Registering...' : 'Register Phone'}
            </button>
          </div>
        </div>
      )}

      {/* Tracking Tab */}
      {activeTab === 'tracking' && (
        <div style={styles.tabContent}>
          {/* Location Status Toggle */}
          <div style={styles.locationToggleContainer}>
            <div style={styles.locationStatusRow}>
              <span style={styles.locationStatusLabel}>📍 Device Location:</span>
              <div style={styles.locationToggle}>
                <span style={{
                  ...styles.locationStatus,
                  backgroundColor: locationEnabled ? '#d4edda' : '#f8d7da',
                  color: locationEnabled ? '#155724' : '#721c24',
                  border: `1px solid ${locationEnabled ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {checkingLocation ? '⏳ Checking...' : (locationEnabled ? '🟢 ON' : '🔴 OFF')}
                </span>
                <button
                  onClick={toggleLocation}
                  style={{
                    ...styles.settingsSmallButton,
                    backgroundColor: locationEnabled ? '#dc3545' : '#ff9800'
                  }}
                  title={locationEnabled ? "Turn off location" : "Turn on location"}
                >
                  {locationEnabled ? '🔴 Turn Off' : '🟢 Turn On'}
                </button>
                {locationEnabled && (
                  <button
                    onClick={refreshLocation}
                    disabled={loading}
                    style={styles.refreshSmallButton}
                    title="Refresh location"
                  >
                    🔄 Refresh
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Refresh Location Button */}
          <div style={styles.locationHeader}>
            <button
              onClick={refreshLocation}
              disabled={loading || !locationEnabled}
              style={{
                ...styles.button,
                backgroundColor: '#ff9800',
                marginBottom: '15px',
                opacity: (!locationEnabled || loading) ? 0.6 : 1,
                cursor: (!locationEnabled || loading) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '📍 Getting location...' : '📍 Get My Location & Track'}
            </button>
          </div>

          <div style={styles.trackingInputs}>
            <input
              type="text"
              placeholder="Enter Phone Number to Track"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              style={styles.input}
            />
            <button
              onClick={() => trackPhoneByNumber(phoneNumber)}
              disabled={loading || !phoneNumber}
              style={styles.trackButton}
            >
              {loading ? '🔄 Tracking...' : '🎯 Track Phone'}
            </button>
            
            <div style={styles.orDivider}>OR</div>
            
            <input
              type="text"
              placeholder="Enter IMEI to Track"
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              style={styles.input}
            />
            <button
              onClick={() => trackPhoneByIMEI(imei)}
              disabled={loading || !imei}
              style={styles.trackButton}
            >
              {loading ? '🔄 Tracking...' : '🎯 Track by IMEI'}
            </button>
          </div>

          {mapError && mapError.includes('not activated') ? renderMapActivationGuide() : mapError && (
            <div style={{...styles.error, marginBottom: '15px'}}>{mapError}</div>
          )}

          <div id="map" style={styles.mapContainer}></div>

          {trackingData && trackingData.success && (
            <div style={styles.resultsContainer}>
              <h3 style={styles.resultsTitle}>📍 Tracking Results</h3>
              
              {trackingData.location && (
                <div style={styles.resultCard}>
                  <h4>📍 Location</h4>
                  <p><strong>Latitude:</strong> {trackingData.location.coordinates.lat.toFixed(6)}</p>
                  <p><strong>Longitude:</strong> {trackingData.location.coordinates.lng.toFixed(6)}</p>
                  <p><strong>Accuracy:</strong> ±{trackingData.location.accuracy}m</p>
                  <p><strong>Source:</strong> {trackingData.location.source}</p>
                  <p><strong>Time:</strong> {new Date(trackingData.location.timestamp).toLocaleString()}</p>
                  <a
                    href={trackingData.location.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.mapLink}
                  >
                    Open in Google Maps
                  </a>
                </div>
              )}
              
              {trackingData.targetDevice && (
                <div style={styles.resultCard}>
                  <h4>📱 Device Info</h4>
                  <p><strong>IP:</strong> {trackingData.targetDevice.ip || 'Unknown'}</p>
                  <p><strong>MAC:</strong> {trackingData.targetDevice.mac || 'Unknown'}</p>
                  <p><strong>Vendor:</strong> {trackingData.targetDevice.vendor || 'Unknown'}</p>
                  <p><strong>Status:</strong> {trackingData.targetDevice.online ? '🟢 Online' : '🔴 Offline'}</p>
                </div>
              )}
              
              {trackingData.scanInfo && (
                <div style={styles.resultCard}>
                  <h4>🌐 Network Scan</h4>
                  <p><strong>Network:</strong> {trackingData.scanInfo.networkScanned}</p>
                  <p><strong>Devices Found:</strong> {trackingData.scanInfo.devicesFound}</p>
                  <p><strong>Scan Time:</strong> {trackingData.scanInfo.scanTimeMs}ms</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Call Tracking Tab */}
      {activeTab === 'call-track' && (
        <div style={styles.tabContent}>
          <h2 style={styles.subtitle}>📞 Track Call Between Two Phones</h2>
          <div style={styles.trackingInputs}>
            <input
              type="text"
              placeholder="Caller IMEI"
              value={callerImei}
              onChange={(e) => setCallerImei(e.target.value)}
              style={styles.input}
            />
            <input
              type="text"
              placeholder="Receiver IMEI"
              value={receiverImei}
              onChange={(e) => setReceiverImei(e.target.value)}
              style={styles.input}
            />
            <button
              onClick={trackCall}
              disabled={loading || (!callerImei && !receiverImei)}
              style={styles.trackButton}
            >
              {loading ? 'Tracking...' : 'Track Call'}
            </button>
          </div>
          <div id="map" style={styles.mapContainer}></div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div style={styles.success}>
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      {/* Error Message - Now properly renders JSX */}
      {errorMessage && (
        <div style={styles.error}>
          {errorMessage}
        </div>
      )}

      {/* Status */}
      <div style={styles.status}>
        {isConnected ? "✅ Bluetooth connected" : bluetoothSupported ? "🔴 Bluetooth disconnected" : "⚠️ Bluetooth not supported"}
        {mapError ? " | 🗺️ Map unavailable" : mapLoaded ? " | 🗺️ Map ready" : " | 🗺️ Loading map..."}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif'
  },
  title: {
    textAlign: 'center',
    color: '#333',
    marginBottom: '30px'
  },
  tabContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  tab: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    backgroundColor: '#f0f0f0',
    transition: 'all 0.3s'
  },
  activeTab: {
    backgroundColor: '#007bff',
    color: 'white'
  },
  tabContent: {
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    minHeight: '500px'
  },
  buttonContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px'
  },
  button: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'background-color 0.3s'
  },
  dangerButton: {
    backgroundColor: '#dc3545'
  },
  subtitle: {
    color: '#555',
    marginBottom: '20px'
  },
  deviceList: {
    listStyle: 'none',
    padding: 0
  },
  deviceItem: {
    padding: '8px',
    margin: '5px 0',
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  connectedInfo: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#d4edda',
    borderRadius: '5px',
    borderLeft: '4px solid #28a745'
  },
  trackingInputs: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '5px'
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    marginBottom: '10px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  trackButton: {
    padding: '12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.3s'
  },
  mapContainer: {
    height: '400px',
    width: '100%',
    marginBottom: '20px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #ddd'
  },
  resultsContainer: {
    marginTop: '20px'
  },
  resultsTitle: {
    color: '#333',
    marginBottom: '15px'
  },
  resultCard: {
    padding: '15px',
    marginBottom: '15px',
    backgroundColor: 'white',
    borderRadius: '5px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  mapLink: {
    display: 'inline-block',
    padding: '8px 16px',
    backgroundColor: '#007bff',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '4px',
    marginTop: '10px'
  },
  orDivider: {
    textAlign: 'center',
    color: '#666',
    fontWeight: 'bold',
    margin: '5px 0'
  },
  locationHeader: {
    marginBottom: '15px'
  },
  locationToggleContainer: {
    backgroundColor: '#f0f0f0',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '15px',
    border: '1px solid #ddd'
  },
  locationStatusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '10px'
  },
  locationStatusLabel: {
    fontWeight: 'bold',
    fontSize: '16px'
  },
  locationToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  locationStatus: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  settingsButton: {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  settingsSmallButton: {
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  refreshSmallButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  errorContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  success: {
    color: '#155724',
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#d4edda',
    borderRadius: '4px',
    border: '1px solid #c3e6cb'
  },
  error: {
    color: '#721c24',
    marginTop: '10px',
    padding: '15px',
    backgroundColor: '#f8d7da',
    borderRadius: '4px',
    border: '1px solid #f5c6cb'
  },
  status: {
    marginTop: '20px',
    padding: '10px',
    textAlign: 'center',
    backgroundColor: '#e9ecef',
    borderRadius: '4px'
  }
};

export default BluetoothDialer;